import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { loader } from "graphql.macro";
import { useQuery } from "@apollo/client";
import {
  ENTERPRISE_CONTACT,
  Interval,
  MAX_SEATS,
  PaidPlan,
  addonLookupKey,
  checkoutPath,
  formatEuros,
  perMonth,
  planLookupKey,
  pricesByKey,
} from "../../lib/plans";
import styles from "./Plans.module.scss";

const GET_PLAN_PRICES = loader("../../schema/query/GetPlanPrices.graphql");

export type PlanColumn = "free" | PaidPlan | "enterprise";

export interface PlanActionContext {
  interval: Interval;
  // Voice Trust+ ticked in the card
  addon: boolean;
}

interface Props {
  // the plan of the organization, its card is highlighted. None on the public
  // pricing page.
  currentPlan?: Plan;
  // and its Voice Trust+ and interval, the initial state of the cards
  currentAddon?: boolean;
  initialInterval?: Interval;
  // the button of a card, the public one (Subscribe...) if not given or if it
  // returns undefined
  renderAction?: (
    column: PlanColumn,
    context: PlanActionContext
  ) => React.ReactNode | undefined;
}

// The pricing table of kizwork.com/pricing: Free demo, Starter, Pro Security,
// Enterprise Fortress, the Voice Trust+ add-on in the Starter and Pro Security
// cards, a monthly / annual switch. The prices come from Stripe (by lookup
// key), excluding VAT, per user and per month.
const Plans: React.FC<Props> = ({
  currentPlan,
  currentAddon = false,
  initialInterval = "monthly",
  renderAction,
}) => {
  const intl = useIntl();
  const { data } = useQuery<{ planPrices: PlanPrice[] }>(GET_PLAN_PRICES);
  const prices = pricesByKey(data?.planPrices);
  const [interval, setPeriod] = useState<Interval>(initialInterval);
  const [addons, setAddons] = useState<Record<PaidPlan, boolean>>({
    starter: currentPlan === "STARTER" && currentAddon,
    pro_security: currentPlan === "PRO_SECURITY" && currentAddon,
  });

  const euros = (amount: number) => formatEuros(intl.locale, amount);
  const monthly = (lookupKey: string) => perMonth(prices[lookupKey], interval);
  const addonPrice = monthly(addonLookupKey(interval));

  const action = (column: PlanColumn, addon = false) =>
    renderAction?.(column, { interval, addon }) ??
    defaultAction(column, interval, addon);

  const per =
    interval === "monthly" ? (
      <FormattedMessage
        id="plans.per-user-month-billed-monthly"
        defaultMessage="per user per month · billed monthly"
      />
    ) : (
      <FormattedMessage
        id="plans.per-user-month-billed-annually"
        defaultMessage="per user per month · billed annually"
      />
    );

  const paidCard = (plan: PaidPlan) => {
    const addon = addons[plan];
    const base = monthly(planLookupKey(plan, interval));
    const current =
      (plan === "starter" && currentPlan === "STARTER") ||
      (plan === "pro_security" && currentPlan === "PRO_SECURITY");
    const classes = [styles.plan];
    if (plan === "pro_security") classes.push(styles.hot);
    if (current) classes.push(styles.current);

    return (
      <div className={classes.join(" ")} key={plan}>
        {current ? (
          <CurrentTag />
        ) : (
          plan === "pro_security" && (
            <span className={styles.tag}>
              <FormattedMessage
                id="plans.most-popular"
                defaultMessage="Most popular"
              />
            </span>
          )
        )}
        <h3>{plan === "starter" ? "Starter" : "Pro Security"}</h3>
        <div className={styles.price}>
          {euros(base + (addon ? addonPrice : 0))}{" "}
          <small>
            <FormattedMessage
              id="plans.per-user-month-short"
              defaultMessage="/user/mo"
            />
          </small>
        </div>
        <p className={styles.per}>{per}</p>
        <div className={styles.vtSum}>
          {addon && (
            <FormattedMessage
              id="plans.incl-voice-trust-plus"
              defaultMessage="incl. Voice Trust+ {price}"
              values={{ price: euros(addonPrice) }}
            />
          )}
        </div>
        {plan === "starter" ? (
          <>
            <p className={styles.desc}>
              <FormattedMessage
                id="plans.starter.desc"
                defaultMessage="Sovereign AI meetings for everyday teams."
              />
            </p>
            <ul>
              <li>
                <FormattedMessage
                  id="plans.starter.video"
                  defaultMessage="Unlimited HD video, 25 participants"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.starter.transcription"
                  defaultMessage="Real-time multilingual transcription"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.starter.translation"
                  defaultMessage="Live translation, 47 languages"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.starter.minutes"
                  defaultMessage="AI Smart Minutes & reports"
                />
              </li>
              <li className={styles.no}>
                <FormattedMessage
                  id="plans.deepfake-detection"
                  defaultMessage="Deepfake detection"
                />
              </li>
            </ul>
          </>
        ) : (
          <>
            <p className={styles.desc}>
              <FormattedMessage
                id="plans.pro.desc"
                defaultMessage="For regulated firms that need voice trust."
              />
            </p>
            <ul>
              <li>
                <FormattedMessage
                  id="plans.pro.everything-in-starter"
                  defaultMessage="Everything in Starter, 100 participants"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.pro.vocabulary"
                  defaultMessage="Custom business vocabulary"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.pro.deepfake"
                  defaultMessage="Deepfake detection on every call — 99.1% accuracy, alert in 2–3 s, 2FA challenge, on by default"
                />
              </li>
              <li>
                <FormattedMessage
                  id="plans.pro.sso"
                  defaultMessage="SSO (AD/SAML) & audit logs"
                />
                <span className={styles.soon}>
                  <FormattedMessage
                    id="plans.q4-2026"
                    defaultMessage="Q4 2026"
                  />
                </span>
              </li>
            </ul>
          </>
        )}
        <label className={styles.vtBox}>
          <input
            type="checkbox"
            checked={addon}
            onChange={(event) =>
              setAddons({ ...addons, [plan]: event.target.checked })
            }
          />
          <span>
            {plan === "starter" ? (
              <FormattedMessage
                id="plans.starter.add-voice-trust-plus"
                defaultMessage="<b>Add Voice Trust+</b> — +{price}/user/mo. Your own voice in 47 languages, certified voice identity, noise filtering."
                values={{
                  b: (...chunks: React.ReactNode[]) => <b>{chunks}</b>,
                  price: euros(addonPrice),
                }}
              />
            ) : (
              <FormattedMessage
                id="plans.pro.add-voice-trust-plus"
                defaultMessage="<b>Add Voice Trust+</b> — +{price}/user/mo. Recommended with Pro: detection proves the voice is human, identity proves it is them."
                values={{
                  b: (...chunks: React.ReactNode[]) => <b>{chunks}</b>,
                  price: euros(addonPrice),
                }}
              />
            )}
          </span>
        </label>
        {action(plan, addon)}
      </div>
    );
  };

  return (
    <div className={styles.pricing}>
      <div
        className={styles.toggle}
        role="group"
        aria-label={intl.formatMessage({
          id: "plans.billing-period",
          defaultMessage: "Billing period",
        })}
      >
        <button
          type="button"
          className={interval === "monthly" ? styles.on : undefined}
          onClick={() => setPeriod("monthly")}
        >
          <FormattedMessage id="plans.monthly" defaultMessage="Monthly" />
        </button>
        <button
          type="button"
          className={interval === "yearly" ? styles.on : undefined}
          onClick={() => setPeriod("yearly")}
        >
          <FormattedMessage
            id="plans.annual-save"
            defaultMessage="Annual — save up to 20%"
          />
        </button>
      </div>

      <div className={styles.plans}>
        <div
          className={[
            styles.plan,
            currentPlan === "FREE" ? styles.current : "",
          ].join(" ")}
        >
          {currentPlan === "FREE" && <CurrentTag />}
          <h3>
            <FormattedMessage
              id="plans.free.title"
              defaultMessage="Free demo"
            />
          </h3>
          <div className={styles.price}>
            {intl.formatNumber(0, {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
            })}
          </div>
          <p className={styles.per}>
            <FormattedMessage
              id="plans.free.per"
              defaultMessage="the whole platform · up to 10 demos a month"
            />
          </p>
          <p className={styles.desc}>
            <FormattedMessage
              id="plans.free.desc"
              defaultMessage="Every feature, live, no card required."
            />
          </p>
          <ul>
            <li>
              <FormattedMessage
                id="plans.free.video"
                defaultMessage="EU-hosted HD video"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.free.meetings"
                defaultMessage="15-minute meetings, up to 2 participants"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.free.minutes"
                defaultMessage="AI minutes and transcripts"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.free.translation-own-voice"
                defaultMessage="Live translation in 47 languages, with speech-to-speech in your own voice"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.free.deepfake"
                defaultMessage="Deepfake detection: each voice shown as human or synthetic within 2–3 s"
              />
            </li>
          </ul>
          {action("free")}
        </div>

        {paidCard("starter")}
        {paidCard("pro_security")}

        <div
          className={[
            styles.plan,
            currentPlan === "ENTERPRISE" ? styles.current : "",
          ].join(" ")}
        >
          {currentPlan === "ENTERPRISE" && <CurrentTag />}
          <h3>Enterprise Fortress</h3>
          <div className={styles.price}>
            <FormattedMessage
              id="plans.enterprise.price"
              defaultMessage="Custom"
            />
          </div>
          <p className={styles.per}>
            <FormattedMessage
              id="plans.enterprise.per"
              defaultMessage="from {price}/mo + per user"
              values={{
                price: intl.formatNumber(2500, {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                }),
              }}
            />
          </p>
          <p className={styles.desc}>
            <FormattedMessage
              id="plans.enterprise.desc"
              defaultMessage="Government, defence and finance."
            />
          </p>
          <ul>
            <li>
              <FormattedMessage
                id="plans.enterprise.on-premises"
                defaultMessage="Private instance or on-premises — data and GDPR stay in your perimeter"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.enterprise.air-gap"
                defaultMessage="Air-gap capable, local AI models"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.enterprise.keys"
                defaultMessage="Customer-managed keys, HSM"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.enterprise.forensic"
                defaultMessage="Full forensic deepfake reports"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.enterprise.logs"
                defaultMessage="Custom compliance logs"
              />
            </li>
            <li>
              <FormattedMessage
                id="plans.enterprise.support"
                defaultMessage="Sovereign support SLA"
              />
            </li>
          </ul>
          {action("enterprise")}
        </div>
      </div>

      <div className={styles.band}>
        <div>
          <div className={styles.kicker}>
            <FormattedMessage
              id="plans.vt.kicker"
              defaultMessage="Add-on · Starter or Pro Security"
            />
          </div>
          <h3>Voice Trust+</h3>
          <div className={styles.bandPrice}>
            +{euros(perMonth(prices.voice_trust_plus_monthly, "monthly"))}{" "}
            <small>
              <FormattedMessage
                id="plans.vt.price-annual"
                defaultMessage="/user/mo · {price} billed annually"
                values={{
                  price: euros(
                    perMonth(prices.voice_trust_plus_yearly, "yearly")
                  ),
                }}
              />
            </small>
          </div>
          <p style={{ marginTop: 10, fontSize: ".92rem" }}>
            <FormattedMessage
              id="plans.vt.text"
              defaultMessage="Tick it on either plan above. Voice Trust+ certifies <b>your</b> voice and carries it across languages; detecting other people's deepfakes is Pro Security."
              values={{ b: (...chunks: React.ReactNode[]) => <b>{chunks}</b> }}
            />
          </p>
        </div>
        <ul>
          <li>
            <FormattedMessage
              id="plans.vt.speech-to-speech"
              defaultMessage="Speech-to-speech: your own voice in 47 languages, no third-party AI voice"
            />
          </li>
          <li>
            <FormattedMessage
              id="plans.vt.identity"
              defaultMessage="Certified voice identity — 25 sentences, ID-verified"
            />
          </li>
          <li>
            <FormattedMessage
              id="plans.vt.badge"
              defaultMessage="Certified real voice badge for enrolled speakers"
            />
          </li>
          <li>
            <FormattedMessage
              id="plans.vt.anonymisation"
              defaultMessage="Irreversible voice anonymisation"
            />
          </li>
          <li>
            <FormattedMessage
              id="plans.vt.article50"
              defaultMessage="Article 50 marking and consent on every synthesised voice"
            />
          </li>
          <li>
            <FormattedMessage
              id="plans.vt.noise"
              defaultMessage="Real-time noise filtering"
            />
          </li>
        </ul>
      </div>

      <p className={styles.notes}>
        <FormattedMessage
          id="plans.notes.sovereignty"
          defaultMessage="Every plan is EU-hosted and outside the US CLOUD Act — sovereignty is never an upsell."
        />
        <br />
        <FormattedMessage
          id="plans.notes.flat"
          defaultMessage="<b>Flat per-seat pricing:</b> 7 seats = 7 × the plan price, no volume ladder to decode. More than {max} seats? <a>Talk to us about Enterprise Fortress</a>."
          values={{
            max: MAX_SEATS,
            b: (...chunks: React.ReactNode[]) => <b>{chunks}</b>,
            a: (...chunks: React.ReactNode[]) => (
              <a href={ENTERPRISE_CONTACT}>{chunks}</a>
            ),
          }}
        />
        <br />
        <FormattedMessage
          id="plans.notes.vat"
          defaultMessage="Prices per user per month, excl. VAT. VAT is calculated at checkout from your country and VAT number. Annual plans are billed once a year, by card or SEPA Direct Debit."
        />
      </p>
      <div className={styles.lock}>
        🔒{" "}
        <FormattedMessage
          id="plans.lock"
          defaultMessage="Trust is the product. Voice transformation is the premium add-on: Voice Trust+ is available today with any Starter or Pro Security seat."
        />
      </div>
    </div>
  );
};

const CurrentTag = () => (
  <span className={`${styles.tag} ${styles.currentTag}`}>
    <FormattedMessage id="plans.current-plan" defaultMessage="Current plan" />
  </span>
);

// The buttons of the public pricing page
const defaultAction = (
  column: PlanColumn,
  interval: Interval,
  addon: boolean
) => {
  switch (column) {
    case "free":
      return (
        <a className={styles.btn} href="/">
          <FormattedMessage
            id="plans.free.cta"
            defaultMessage="Try the whole platform"
          />
        </a>
      );
    case "enterprise":
      return (
        <a
          className={styles.btn}
          href="mailto:contact@kizwork.com?subject=Enterprise%20Fortress%20quote"
        >
          <FormattedMessage
            id="plans.enterprise.cta"
            defaultMessage="Talk to sales"
          />
        </a>
      );
    default:
      return (
        <a
          className={
            column === "pro_security"
              ? `${styles.btn} ${styles.btnPrimary}`
              : styles.btn
          }
          href={checkoutPath(column, interval, addon)}
        >
          <FormattedMessage id="plans.subscribe" defaultMessage="Subscribe" />
        </a>
      );
  }
};

export const PlanButton: React.FC<{
  primary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  href?: string;
  children: React.ReactNode;
}> = ({ primary, disabled, onClick, href, children }) => {
  const className = primary ? `${styles.btn} ${styles.btnPrimary}` : styles.btn;
  return href ? (
    <a className={className} href={href}>
      {children}
    </a>
  ) : (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export default Plans;
